import { AxiosResponse } from 'axios';
import ApiUrls from '../config/ApiUrls';
import { BillingStatus } from '../data/models/Group';
import { ReconciliationLogApiResponse } from '../data/models/ReconciliationLog';
import { User } from '../data/models/User';
import { replaceByObject } from '../utils/Urls';
import API from './Api';
import {
  PaymentApiResponse,
  Plan,
  SubscriptionApiResponse,
} from './BillingApi';
import { EntityApiResponse } from './EntityApi';
import { UserApiResponse, UserPermission } from './UserApi';
import { CurrencyCode } from '../config/CurrencyCode';
import { BillingFrequency } from '../config/Constants';
import {
  LoanAccountsUpdateApiRequest,
  LoanAccountType,
} from '../data/models/LoanAccounts';
import { DateTime } from 'luxon';
import { RulesApiResponse } from './RuleApi';
import { EnabledFeatures } from '../data/models/Limits';

export type GroupUpdateApiRequest = {
  name: string;
  hasOnboarded: boolean;
  startDate: string;
  balancerSettings: BalancerSettingsResponse;
  hasBrag: boolean;
  hasRecharger: boolean;
  hasLoans: boolean;
};

export type GroupApiResponse = GroupUpdateApiRequest & {
  id: number;
  billingStatus: BillingStatus;
  trialEnd: string;
  organisations: EntityApiResponse[];
  archivedOrganisations: EntityApiResponse[];
  hasBrag: boolean;
  hasRecharger: boolean;
  hasLoans: boolean;
  is202310PlanOverride: boolean;
};

export type BalancerSettingsResponse = {
  lenderOrgs: number[];
  requiredCurrencies: CurrencyCode[];
  balanceDiscrepancy: number;
};

export type SuccessfulGroupResponse = {
  status: string;
  group: GroupApiResponse;
};

export type GroupUserResponse = {
  permission: UserPermission;
  user: UserApiResponse;
};

export type AccountMappingsApiResponse = {
  fromId: number;
  toId: number;
  type: string;
  code: string;
  id: number;
};

export type ConversionBalanceApiResponse = {
  organisationId: number;
  nominalCode: string; // external ID
  date: string;
  balance: number;
};

export enum BillingDelegateType {
  USER = 'user',
  GROUP = 'group',
}

export type BillingDelegateApiResponse = {
  delegateType: BillingDelegateType;
  delegateId: number;
};

export type LimitsApiResponse = {
  limited: boolean;
  rulesLimited: boolean;
  totalCarriedOverAllowance: number;
  totalMonthlyAllowance: number;
  usedMonthlyAllowance: number;
  totalRuleAllowance: number;
  enabledFeatures: EnabledFeatures[];
  usedRules: number;
};

export type GroupSubscriptionApiResponse = {
  subscription: SubscriptionApiResponse;
  payments: PaymentApiResponse[];
  billingDelegate: BillingDelegateApiResponse;
  limits: LimitsApiResponse;
};

export type GroupSubscriptionPaddleManagementApiResponse = {
  updatePaymentMethod: string;
  cancel: string;
};

export type GroupSubscriptionInvoiceApiResponse = {
  url: string;
};

export type LoanAccountsApiResponse = {
  id: number;
  created: DateTime;
  org1: {
    id: number;
    accountCode: string;
    contactExternalId?: string;
    ncExternalId?: string;
    isLender: boolean;
  };
  org2: {
    id: number;
    accountCode: string;
    contactExternalId?: string;
    ncExternalId?: string;
    isLender: boolean;
  };
  name: string;
  isRecharger: boolean;
  isBrag: boolean;
  type?: LoanAccountType;
};

export type MoveEntitiesResponse = {
  newGroupId: number;
  organisationIds: number[];
  movedLoanAccounts: LoanAccountsApiResponse[];
  deletedLoanAccounts: LoanAccountsApiResponse[];
  deletedRules: RulesApiResponse[];
};

export class GroupsApi extends API {
  static getGroup(groupId: number): Promise<GroupApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.Group, { groupId: '' + groupId })
    ).then((response: AxiosResponse<GroupApiResponse>) => response.data);
  }

  static create(data: GroupUpdateApiRequest): Promise<GroupApiResponse> {
    return API.post(ApiUrls.Groups, data).then(
      (response: AxiosResponse<GroupApiResponse>) => response.data
    );
  }

  static update(
    groupId: number,
    data: GroupUpdateApiRequest
  ): Promise<GroupApiResponse> {
    return API.put(replaceByObject(ApiUrls.Group, { groupId }), data).then(
      (response: AxiosResponse<GroupApiResponse>) => response.data
    );
  }

  static updateTrialEnd(
    groupId: number,
    trialEndDate: string
  ): Promise<GroupApiResponse> {
    return API.put(
      replaceByObject(ApiUrls.GroupTrialEndDate, { groupId }),
      {},
      {
        params: { trialEndDate },
      }
    ).then((response: AxiosResponse<GroupApiResponse>) => response.data);
  }

  static getUsers(groupId: number): Promise<GroupUserResponse[]> {
    return API.get(replaceByObject(ApiUrls.GroupUsers, { groupId })).then(
      (response: AxiosResponse<GroupUserResponse[]>) => response.data
    );
  }

  static removeUser(groupId: number, userId: number): Promise<void> {
    return API.delete(
      replaceByObject(ApiUrls.GroupUser, { groupId, userId })
    ).then((response: AxiosResponse<void>) => response.data);
  }

  static invite(groupId: number, email: string): Promise<User[]> {
    return API.post(replaceByObject(ApiUrls.GroupUsers, { groupId }), {
      email,
    }).then((response: AxiosResponse<User[]>) => response.data);
  }

  static archiveEntity(groupId: number, entityId: number): Promise<void> {
    return API.put(
      replaceByObject(ApiUrls.EntityArchive, { groupId, entityId }),
      {}
    ).then((response: AxiosResponse<void>) => response.data);
  }

  static restoreEntity(groupId: number, entityId: number): Promise<void> {
    return API.put(
      replaceByObject(ApiUrls.EntityRestore, { groupId, entityId }),
      {}
    ).then((response: AxiosResponse<void>) => response.data);
  }

  static removeEntity(groupId: number, entityId: number): Promise<void> {
    return API.delete(
      replaceByObject(ApiUrls.Entity, { groupId, entityId })
    ).then((response: AxiosResponse<void>) => response.data);
  }

  static getReconciliationLog(
    groupId: number
  ): Promise<ReconciliationLogApiResponse[]> {
    return API.get(
      replaceByObject(ApiUrls.ReconciliationLog, { groupId })
    ).then(
      (response: AxiosResponse<ReconciliationLogApiResponse[]>) => response.data
    );
  }

  static getAccountMappings(
    groupId: number
  ): Promise<AccountMappingsApiResponse[]> {
    return API.get(replaceByObject(ApiUrls.AccountMappings, { groupId })).then(
      (response: AxiosResponse<AccountMappingsApiResponse[]>) => response.data
    );
  }

  static setAccountMappings(
    groupId: number,
    mappings: AccountMappingsApiResponse[]
  ): Promise<AccountMappingsApiResponse[]> {
    return API.put(
      replaceByObject(ApiUrls.AccountMappings, { groupId }),
      mappings
    ).then(
      (response: AxiosResponse<AccountMappingsApiResponse[]>) => response.data
    );
  }

  static getConversionBalances(
    groupId: number
  ): Promise<ConversionBalanceApiResponse[]> {
    return API.get(
      replaceByObject(ApiUrls.ConversionBalances, { groupId })
    ).then(
      (response: AxiosResponse<ConversionBalanceApiResponse[]>) => response.data
    );
  }

  static setConversionBalances(
    groupId: number,
    balances: ConversionBalanceApiResponse[]
  ): Promise<ConversionBalanceApiResponse[]> {
    return API.put(
      replaceByObject(ApiUrls.ConversionBalances, { groupId }),
      balances
    ).then(
      (response: AxiosResponse<ConversionBalanceApiResponse[]>) => response.data
    );
  }

  static getSubscription(
    groupId: number
  ): Promise<GroupSubscriptionApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.GroupSubscription, { groupId })
    ).then(
      (response: AxiosResponse<GroupSubscriptionApiResponse>) => response.data
    );
  }

  static getSubscriptionManagementUrls(
    groupId: number
  ): Promise<GroupSubscriptionPaddleManagementApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.GroupSubscriptionManagementUrls, { groupId })
    ).then(
      (response: AxiosResponse<GroupSubscriptionPaddleManagementApiResponse>) =>
        response.data
    );
  }

  static getSubscriptionInvoice(
    groupId: number,
    paymentId: number
  ): Promise<GroupSubscriptionInvoiceApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.GroupSubscriptionInvoice, { groupId, paymentId })
    ).then(
      (response: AxiosResponse<GroupSubscriptionInvoiceApiResponse>) =>
        response.data
    );
  }

  static updateSubscription(
    groupId: number,
    plan: Plan,
    billingFrequency: BillingFrequency,
    rechargerAddonCount: number = 0,
    bragAddonCount: number = 0
  ): Promise<SubscriptionApiResponse> {
    return API.patch(replaceByObject(ApiUrls.GroupSubscription, { groupId }), {
      plan,
      billingFrequency,
      addons: {
        recharger: rechargerAddonCount,
        brag: bragAddonCount,
      },
    }).then(
      (response: AxiosResponse<SubscriptionApiResponse>) => response.data
    );
  }

  static getLoanAccounts(groupId: number): Promise<LoanAccountsApiResponse[]> {
    return API.get(replaceByObject(ApiUrls.LoanAccounts, { groupId })).then(
      (response: AxiosResponse<LoanAccountsApiResponse[]>) => response.data
    );
  }

  static setLoanAccounts(
    groupId: number,
    loanAccounts: LoanAccountsUpdateApiRequest[]
  ): Promise<LoanAccountsApiResponse[]> {
    return API.put(
      replaceByObject(ApiUrls.LoanAccounts, { groupId }),
      loanAccounts
    ).then(
      (response: AxiosResponse<LoanAccountsApiResponse[]>) => response.data
    );
  }

  static moveEntities(
    groupId: number,
    organisationIds: number[],
    newGroupId: number,
    dryRun: Boolean
  ): Promise<MoveEntitiesResponse> {
    return API.put(
      replaceByObject(ApiUrls.MoveEntities, { groupId }),
      {
        newGroupId,
        organisationIds,
        dryRun,
      },
      {
        params: { dryRun },
      }
    ).then((response: AxiosResponse<MoveEntitiesResponse>) => response.data);
  }
}
